"use client";

import { useCallback, useEffect, useState } from "react";

import { AssetTypes } from "@zapier/utils";
import { useCheckPermissions } from "./useCheckPermissions";
import { useAccountActionPermissions } from "./useAccountActionPermissions";

/**
 * Returns what actions a user can perform on a Chatbot.
 *
 */
export function useChatbotActionPermissions(chatbotId: string) {
  // a toggleable state to opt in for fetching permissions
  const [permissionsCheckEnabled, setPermissionsCheckEnabled] = useState(false);

  const enablePermissionsCheck = useCallback(() => {
    setPermissionsCheckEnabled(true);
  }, []);

  const { permissions, checkPermissions, isLoading } = useCheckPermissions({
    assetId: chatbotId,
    assetType: AssetTypes.Chatbot,
    permissionLevels: ["can_edit", "can_delete", "can_change_owner"],
  });

  const { allowTeams: hasTeams } = useAccountActionPermissions();

  useEffect(() => {
    if (permissionsCheckEnabled && !permissions && !isLoading) {
      checkPermissions();
    }
  }, [isLoading, permissions, permissionsCheckEnabled, checkPermissions]);

  return {
    canDelete: permissions?.canDelete || false,
    canMove: permissions?.canEdit || false,
    canRename: permissions?.canEdit || false,
    canChangeOwner: (permissions?.canChangeOwner && hasTeams) || false,
    canShare: (permissions?.canEdit && hasTeams) || false,
    canDuplicate: true, // anyone with view access can duplicate a chatbot
    enablePermissionsCheck,
    isLoading,
    hasTeams,
  };
}
